import React, { useEffect, useState } from 'react';

import { trackContactFormSubmit } from '@/utils/analytics';

import useContactForm from '../hooks/useContactForm';

/** Contact form card — source: mockup L945–973 (sent state L964–971). */
const ContactForm = (): React.JSX.Element => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);

  const [club, setClub] = useState('');

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const [phone, setPhone] = useState('');

  const [message, setMessage] = useState('');
  const [messageError, setMessageError] = useState(false);

  // `sent` comes from useContactForm and has no reset — it only flips back to
  // `false` when a fresh submission starts. `dismissedSent` lets "Send another
  // message" return to the form without waiting on that. Whenever `sent`
  // itself changes (a new submit cycle), the dismissal is cleared during
  // render — the React-recommended way to reset derived state on a prop
  // change — so a later successful resubmission still shows the sent panel.
  const [dismissedSent, setDismissedSent] = useState(false);
  const [lastSent, setLastSent] = useState(false);

  const { submitContactForm, isLoading, sent, error } = useContactForm();

  if (sent !== lastSent) {
    setLastSent(sent);
    setDismissedSent(false);
  }

  const showSent = sent && !dismissedSent;

  // Track conversion when the form is successfully submitted.
  useEffect(() => {
    if (sent) {
      trackContactFormSubmit();
    }
  }, [sent]);

  const resetForm = (): void => {
    setName('');
    setClub('');
    setEmail('');
    setPhone('');
    setMessage('');
    setNameError(false);
    setEmailError(false);
    setMessageError(false);
    setDismissedSent(true);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();

    setNameError(!name);
    setEmailError(!email);
    setMessageError(!message);

    if (!name || !email || !message) {
      return;
    }

    const body = club ? `Club: ${club}\n\n${message}` : message;
    void submitContactForm(name, email, phone, body);
  };

  if (showSent) {
    return (
      <div className="oq-contact-form__sent">
        <div className="oq-contact-form__sent-icon">
          <i className="mdi mdi-check" aria-hidden="true" />
        </div>
        <h2 className="oq-contact-form__title oq-bebas">Thanks — message sent</h2>
        <p className="oq-contact-form__sent-copy">
          We&apos;ve got it and will be back to you within one working day. Fancy a faster answer?
          Try WhatsApp.
        </p>
        <button type="button" className="oq-contact-form__reset" onClick={resetForm}>
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="oq-contact-form__title oq-bebas">Send us a message</h2>
      <p className="oq-contact-form__subtitle">We usually reply within one working day.</p>
      <form onSubmit={handleSubmit} noValidate>
        <div className="oq-contact-form__row">
          <div className="oq-contact-form__field">
            <label htmlFor="contact-name" className="oq-contact-form__label">
              Your name
            </label>
            <input
              id="contact-name"
              type="text"
              placeholder="Jane Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="oq-contact-form__input"
            />
            {nameError && <p className="oq-contact-form__error">Your name is required</p>}
          </div>
          <div className="oq-contact-form__field">
            <label htmlFor="contact-club" className="oq-contact-form__label">
              Club name
            </label>
            <input
              id="contact-club"
              type="text"
              placeholder="Your club"
              value={club}
              onChange={(e) => setClub(e.target.value)}
              className="oq-contact-form__input"
            />
          </div>
        </div>
        <div className="oq-contact-form__field">
          <label htmlFor="contact-email" className="oq-contact-form__label">
            Email
          </label>
          <input
            id="contact-email"
            type="email"
            placeholder="jane@yourclub.co.uk"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="oq-contact-form__input"
          />
          {emailError && <p className="oq-contact-form__error">Email address is required</p>}
        </div>
        <div className="oq-contact-form__field">
          <label htmlFor="contact-phone" className="oq-contact-form__label">
            Phone number <span className="oq-contact-form__optional">(optional)</span>
          </label>
          <input
            id="contact-phone"
            type="tel"
            placeholder="07123 456789"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="oq-contact-form__input"
          />
        </div>
        <div className="oq-contact-form__field oq-contact-form__field--message">
          <label htmlFor="contact-message" className="oq-contact-form__label">
            How can we help?
          </label>
          <textarea
            id="contact-message"
            rows={5}
            placeholder="Tell us a little about your club and what you're looking for…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="oq-contact-form__textarea"
          />
          {messageError && <p className="oq-contact-form__error">A message is required</p>}
        </div>
        <button type="submit" className="oq-contact-form__submit" disabled={isLoading}>
          {isLoading ? 'Sending…' : 'Send message'}
        </button>
        <p className="oq-contact-form__microcopy">
          By sending, you agree to be contacted about Olympiq. We never share your details.
        </p>
        {error && (
          <p className="oq-contact-form__form-error">Something went wrong. Please try again.</p>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
